
import { Component, Vue, Watch, Prop } from "vue-property-decorator"
import { namespace, Getter, Action } from "vuex-class"

import vSelect from "vue-select"
import FormGroup from "@/components/Fields/FormGroup.vue"
import UserImage from "@/components/UserImage.vue"
import UserSearch from "./UserSearch.vue"

import { Field, CaseDetails, User, CaseUser } from "@/types"

import api from "@/services/api"
import { UserIdWithStatusAndFields } from "@/types/user"
import UserHeaderFields from "./UserHeaderFields.vue"
const caseStore = namespace("cases")

@Component({
	components: {
		"form-group": FormGroup,
		"v-select": vSelect,
		"user-image": UserImage,
		"user-search": UserSearch,
		"user-role": () => import("./UserRole.vue"),
		"user-header-fields": UserHeaderFields
	}
})
export default class UserRole extends Vue {
	@Prop()
	title!: string;

	@Prop()
	userRole!: any;

	@Prop()
	value: any;

	@Prop()
	extensions: any;

	@Prop({ type: Boolean, default: false })
	readonly!: boolean;

	@Prop()
	public currentCase!: CaseDetails;

	newUsers: CaseUser[] = [];
	addingUser: boolean = false;
	addingExternalUser: boolean = false;
	addingToRole: any = null;

	confirmRemoveUserId: string | null = null
	userDataByUserId: { [userId:string] : UserIdWithStatusAndFields } = {}

	isMounted = false

	public get users (): any[] {
		const users = this.userValues.map(user => {
			let userData = this.userDataByUserId![user.id]
			let headerValues = userData && userData.userHeaderFieldValues ? userData.userHeaderFieldValues : {}

			if (this.currentCase && this.currentCase.users) {
				let caseUser: any = this.currentCase.users.find(u => u.id === user.id)
				if (caseUser) {
					return {
						id: caseUser.id,
						name: caseUser.name,
						teams: caseUser.teams,
						email: caseUser.email,
						isExternal: caseUser.isExternal,
						timeSpent: user.timeSpent,
						userHeaderFieldValues: headerValues
					}
				}
			}

			if (this.newUsers) {
				const newUser = this.newUsers.find(u => u.id === user.id)
				if (newUser) {
					return {
						id: newUser.id,
						name: newUser.name,
						teams: newUser.teams,
						email: newUser.email,
						isExternal: newUser.isExternal,
						timeSpent: user.timeSpent,
						userHeaderFieldValues: headerValues
					}
				}
			}

			return {
				id: user.id,
				name: "",
				teams: [],
				timeSpent: user.timeSpent,
				userHeaderFieldValues: headerValues
			}
		})
		return users
	}

	get userValues (): any[] {
		return this.value && this.value.users ? this.value.users : []
	}

	get userIds (): string[] {
		return this.userValues.map(user => user.id)
	}

	get subroles () {
		if (!this.userRole || !this.userRole.subroles) return []

		return this.userRole.subroles.map((subrole: any) => {
			const subroleValues = this.value && this.value.subRoles
				? this.value.subRoles.find((r: any) => r.title === subrole.title)
				: null
			return {
				...subrole,
				users: subroleValues ? subroleValues.users || [] : []
			}
		})
	}

	public async mounted () {
		if (this.value && this.value.users) {
			const usersNotOnCase = this.currentCase && this.currentCase.users
				? this.userValues.filter(user => !this.currentCase.users.find(u => u.id === user.id))
				: this.userValues

			usersNotOnCase.forEach(user => {
				const caseId = this.currentCase ? (this.currentCase.id ? this.currentCase.id : "") : ""
				api.userDetails(user.id, caseId).then(userDetails => {
					if (!this.newUsers.some(u => u.id === userDetails.id)) {
						this.newUsers.push({
							id: userDetails.id,
							name: userDetails.name,
							teams: userDetails.teams,
							email: userDetails.email,
							isExternal: userDetails.isExternal
						})
					}
				})
			})
		}
		await this.bulkLoadUserData()
		this.isMounted = true
	}

	public addUser (user: any) {
		this.addingUser = false

		if (!this.newUsers.some(u => u.id === user.id)) {
			this.newUsers.push({
				id: user.id,
				name: user.name === undefined ? user.title : user.name,
				email: user.email,
				isExternal: user.isExternal,
				teams: user.teams
			})
		}

		const userList = this.userValues
		if (user.isExternal) {
			userList.push({
				id: user.id,
				timeSpent: user.timeSpent,
				name: user.name,
				email: user.email,
				isExternal: user.isExternal
			})
		} else {
			userList.push({
				id: user.id,
				timeSpent: user.timeSpent
			})
		}

		this.$emit("input", {
			...this.value,
			users: userList
		})
	}

	public removeUser (user: User) {
		this.confirmRemoveUserId = null
		this.$emit("input", {
			...this.value,
			users: this.userValues.filter(u => u.id !== user.id)
		})
	}

	public updateRole (role: any, value: any) {
		Vue.set(role, "users", value.users)

		const newValue = {
			...this.value,
			subRoles: this.userRole.subroles.map((subrole: any) => {
				let subroleValues = this.value && this.value.subRoles
					? this.value.subRoles.find((r: any) => r.title === subrole.title)
					: null

				if (!subroleValues) {
					subroleValues = {
						users: []
					}
				}

				return {
					title: subrole.title,
					users: subrole.title === role.title
						? value.users
						: subroleValues.users
				}
			})
		}
		this.$emit("input", newValue)
	}

	public updateUserTime (user: CaseUser, value: any) {
		this.value.users[this.value.users.map((x: { id: any; }) => x.id).indexOf(user.id)] = user
		this.$emit("input", {
			...this.value,
			users: this.userValues
		})
	}

	@Watch("userIds")
	private async bulkLoadUserData () : Promise<void> {
		if (!this.userIds || this.userIds.length === 0) {
			return
		}
		const userIdsWithData = await api.userStatusListWithFieldsGet(this.userIds)

		userIdsWithData.forEach(userIdWithData => {
			this.userDataByUserId![userIdWithData.userId] = userIdWithData
		})
	}
}

